import { ExpenseCategory, CategoryType } from '@/types/database/expense-categories';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Edit, Layers, FolderTree, FileText, Trash2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useDeleteExpenseCategory } from '@/hooks/finance/useExpenseCategories';
import { toast } from 'sonner';
import { BusinessUnitBadge } from '@/components/finance/BusinessUnitSelect';
import { useAllocationsByCategory } from '@/hooks/finance/useCostPositionAllocations';
import { BUSINESS_UNITS } from '@/constants/businessUnits';

interface ExpenseCategoryGroupViewProps {
  categories: ExpenseCategory[];
  onEdit: (category: ExpenseCategory) => void;
  isAdmin?: boolean;
}

interface CategoryNode extends ExpenseCategory {
  children: CategoryNode[];
}

const getCategoryTypeInfo = (type: CategoryType) => {
  switch (type) {
    case 'expense_category':
      return {
        icon: Layers,
        label: 'Expense Category',
        badgeVariant: 'default' as const,
        bgClass: 'bg-primary/5',
      };
    case 'cost_group':
      return {
        icon: FolderTree,
        label: 'Cost Group',
        badgeVariant: 'secondary' as const,
        bgClass: 'bg-muted/30',
      };
    case 'cost_position':
      return {
        icon: FileText,
        label: 'Cost Position',
        badgeVariant: 'outline' as const,
        bgClass: 'bg-background',
      };
    default:
      return {
        icon: FileText,
        label: 'Unknown',
        badgeVariant: 'outline' as const,
        bgClass: '',
      };
  }
};

const getCashFlowLabel = (type: string | null) => {
  switch (type) {
    case 'operating':
      return 'Operating';
    case 'investing':
      return 'Investing';
    case 'financing':
      return 'Financing';
    default:
      return null;
  }
};

export const ExpenseCategoryGroupView = ({ 
  categories, 
  onEdit,
  isAdmin = false
}: ExpenseCategoryGroupViewProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteCategory, setDeleteCategory] = useState<CategoryNode | null>(null);
  const deleteMutation = useDeleteExpenseCategory();
  const { allocationsByCategory } = useAllocationsByCategory();

  // Build a tree structure from flat list
  const buildTree = (cats: ExpenseCategory[]): CategoryNode[] => {
    const map = new Map<string, CategoryNode>();
    const roots: CategoryNode[] = [];

    // First pass: create nodes with empty children
    cats.forEach(cat => {
      map.set(cat.id, { ...cat, children: [] });
    });

    // Second pass: build parent-child relationships
    cats.forEach(cat => {
      const node = map.get(cat.id)!;
      if (cat.parent_id && map.has(cat.parent_id)) {
        map.get(cat.parent_id)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort by name
    const sortNodes = (nodes: CategoryNode[]): CategoryNode[] => {
      return nodes.sort((a, b) => a.name.localeCompare(b.name)).map(node => ({
        ...node,
        children: sortNodes(node.children)
      }));
    };

    return sortNodes(roots);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set(categories.filter(c => 
      categories.some(child => child.parent_id === c.id)
    ).map(c => c.id));
    setExpandedIds(allIds);
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  const handleDelete = async () => {
    if (!deleteCategory) return;
    
    try {
      await deleteMutation.mutateAsync(deleteCategory.id);
      toast.success(`"${deleteCategory.name}" deleted successfully`);
      setDeleteCategory(null);
    } catch (error) {
      // Error is handled in the mutation
    }
  };

  const tree = buildTree(categories);

  const renderCategory = (node: CategoryNode, level: number = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedIds.has(node.id);
    const typeInfo = getCategoryTypeInfo(node.category_type);
    const TypeIcon = typeInfo.icon;
    const cashFlowLabel = getCashFlowLabel(node.cash_flow_type);

    return (
      <div key={node.id} className="w-full">
        <Collapsible open={isExpanded} onOpenChange={() => hasChildren && toggleExpand(node.id)}>
          <div 
            className={cn(
              "flex items-center justify-between py-3 px-4 border-b border-border hover:bg-muted/50 transition-colors",
              typeInfo.bgClass
            )}
            style={{ paddingLeft: `${16 + level * 24}px` }}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {hasChildren ? (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              ) : (
                <div className="w-6 shrink-0" />
              )}
              
              <TypeIcon className={cn(
                "h-4 w-4 shrink-0",
                node.category_type === 'expense_category' && "text-primary",
                node.category_type === 'cost_group' && "text-muted-foreground",
                node.category_type === 'cost_position' && "text-green-600"
              )} />

              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{node.name}</span>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {node.code}
                  </Badge>
                  {!node.is_active && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      Inactive
                    </Badge>
                  )}
                </div>
                {node.description && (
                  <span className="text-xs text-muted-foreground truncate">
                    {node.description}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {node.gl_account_code && (
                <span className="text-sm text-muted-foreground">
                  GL: {node.gl_account_code}
                </span>
              )}
              {cashFlowLabel && (
                <Badge variant="default" className="text-xs">
                  {cashFlowLabel}
                </Badge>
              )}
              {node.is_recordable && (
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  Recordable
                </Badge>
              )}
              {(() => {
                const allocs = allocationsByCategory.get(node.id) || [];
                if (allocs.length > 0) {
                  return (
                    <div className="flex flex-wrap gap-1">
                      {allocs.map((a) => {
                        const bu = BUSINESS_UNITS.find((b) => b.value === a.business_unit);
                        return (
                          <Badge
                            key={a.business_unit}
                            variant="outline"
                            className="text-xs"
                            style={{ borderColor: bu?.color, color: bu?.color }}
                          >
                            {bu?.label} {Number(a.percentage)}%
                          </Badge>
                        );
                      })}
                    </div>
                  );
                }
                if ((node as any).default_business_unit) {
                  return <BusinessUnitBadge value={(node as any).default_business_unit} />;
                }
                return null;
              })()}
              {hasChildren && (
                <Badge variant="secondary" className="text-xs">
                  {node.children.length} sub
                </Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(node);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
              {isAdmin && !hasChildren && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteCategory(node);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {hasChildren && (
            <CollapsibleContent>
              {node.children.map(child => renderCategory(child, level + 1))}
            </CollapsibleContent>
          )}
        </Collapsible>
      </div>
    );
  };

  const expenseCategoryCount = categories.filter(c => c.category_type === 'expense_category').length;
  const costGroupCount = categories.filter(c => c.category_type === 'cost_group').length;
  const costPositionCount = categories.filter(c => c.category_type === 'cost_position').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Layers className="h-4 w-4 text-primary" />
            {expenseCategoryCount} categories
          </span>
          <span className="flex items-center gap-1">
            <FolderTree className="h-4 w-4" />
            {costGroupCount} groups
          </span>
          <span className="flex items-center gap-1">
            <FileText className="h-4 w-4 text-green-600" />
            {costPositionCount} positions
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>
      </div>
      
      <div className="border rounded-lg overflow-hidden bg-card">
        {tree.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No expense categories found. Add your first category to get started.
          </div>
        ) : (
          tree.map(node => renderCategory(node))
        )}
      </div>

      <AlertDialog open={!!deleteCategory} onOpenChange={(open) => !open && setDeleteCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteCategory?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {deleteCategory?.category_type === 'cost_position' ? 'cost position' : 'category'} "{deleteCategory?.name}" ({deleteCategory?.code}).
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
